import React from 'react';
import PropTypes from 'prop-types';

import {Accordion, AccordionDetails, AccordionSummary} from 'views/components';
import {Box, Typography, Paper, Divider, Link, Stack, IconButton} from 'views/components';
import {CallOutlined, StorageOutlined, RuleOutlined, VolumeUp, Stop} from 'views/icons';
import {InvitationInterviewRecording, translate, Translate} from 'views/module';

import moment from 'moment';
import backend from 'utils/backend';

const InterviewCard = (props) => {
  const {interview} = props;

  const [isRecordingPlaying, setIsRecordingPlaying] = React.useState(false);
  const [isRecordingLoading, setIsRecordingLoading] = React.useState(false);
  const [hasRecordingError, setHasRecordingError] = React.useState(false);

  const hasConversation = !!interview.convUrl;
  const isPublicRecording = interview.interviewType?.startsWith('LANGUAGE_');
  const conversationId = interview.conversationId;
  const caregiverRn = interview.caregiverRn;
  // Only the Loom caregiver page sets loomInvitation: Loom stores the recording of a job interview,
  // not the legacy file storage.
  const loomInvitation = interview.loomInvitation;
  const hasRecording = !loomInvitation && Boolean(conversationId && caregiverRn);
  const hasConversationSection = hasConversation || hasRecording || Boolean(loomInvitation);
  const FILE_STORAGE_DOMAIN = process.env.REACT_APP_FILE_STORAGE_DOMAIN;

  const audioRef = React.useRef(null);
  const audioUrlRef = React.useRef(null);

  React.useEffect(() => {
    setIsRecordingPlaying(false);
    setIsRecordingLoading(false);
    setHasRecordingError(false);

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }

    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
  }, [conversationId, caregiverRn, isPublicRecording]);

  React.useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
      }
    };
  }, []);

  const getRecordingAudio = async () => {
    if (audioRef.current) {
      return audioRef.current;
    }

    const audioUrl = `${FILE_STORAGE_DOMAIN}${isPublicRecording ? '/public' : ''}/caregiver/${caregiverRn}/interviews/${conversationId}.mp3`;
    const audioFile = await backend.fileStorage.getSecureFile(audioUrl);
    const downloadedAudioUrl = URL.createObjectURL(audioFile);

    audioUrlRef.current = downloadedAudioUrl;
    audioRef.current = new Audio(downloadedAudioUrl);

    audioRef.current.addEventListener('error', () => {
      setHasRecordingError(true);
      setIsRecordingPlaying(false);
    });
    audioRef.current.addEventListener('ended', () => {
      setIsRecordingPlaying(false);
      audioRef.current.currentTime = 0;
    });

    return audioRef.current;
  };

  const handlePlayRecordingToggle = async () => {
    if (!hasRecording || isRecordingLoading) {
      return;
    }

    if (isRecordingPlaying && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsRecordingPlaying(false);
      return;
    }

    try {
      setIsRecordingLoading(true);
      const audio = await getRecordingAudio();
      await audio.play();
      setHasRecordingError(false);
      setIsRecordingPlaying(true);
    } catch (error) {
      setHasRecordingError(true);
      setIsRecordingPlaying(false);
    } finally {
      setIsRecordingLoading(false);
    }
  };

  const evaluationChip = (status) => {
    if (status === 'success') {
      return {
        bg: '#ecfdf5',
        border: '#6ee7b7',
        text: '#059669',
        label: translate('career.jobDetail.Evaluation.Success'),
      };
    }
    if (status === 'failure') {
      return {
        bg: '#fef2f2',
        border: '#fca5a5',
        text: '#ef4444',
        label: translate('career.jobDetail.Evaluation.Failure'),
      };
    }

    return {
      bg: '#f3f4f6',
      border: '#e5e7eb',
      text: '#6b7280',
      label: translate('career.jobDetail.Evaluation.Unknown'),
    };
  };

  const evalObj = interview.evaluation || {};
  const evaluationTitleMap = {
    basicQualification: 'career.jobDetail.ExternalApplications.BasicEval',
    communication: 'career.jobDetail.ExternalApplications.CommunicationEval',
    motivation: 'career.jobDetail.ExternalApplications.MotivationEval',
  };
  const evaluationEntries = Object.entries(evalObj).filter(([key]) => key !== 'screeningStatus');
  const hasEvaluationEntries = evaluationEntries.length > 0;

  return (
    <Accordion>
      <AccordionSummary
        sx={{
          'alignItems': 'center',
          'minHeight': 'unset',
          'pb': 2,
          'mt': 1,
          '& .MuiAccordionSummary-content': {m: 0},
          '& .MuiAccordionSummary-expandIconWrapper': {
            alignSelf: 'center',
            mr: 2,
          },
        }}
      >
        <Box sx={{display: 'flex', flexDirection: 'column', width: '100%'}}>
          <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography variant="h6" sx={{fontWeight: 600}}>
                {moment(interview.createdAt).format('MMM DD, YYYY h:mm A')}
              </Typography>
            </Stack>
          </Box>

        </Box>
      </AccordionSummary>
      <Divider />
      <AccordionDetails>
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            ml: 4,
            gridTemplateColumns: {xs: '1fr', md: hasConversationSection ? '1fr 280px' : '1fr'},
          }}
        >
          <Box sx={{display: 'grid', gap: 2}}>
            {/* Data Collection Section */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 1.5,
                backgroundColor: '#fff',
                borderColor: '#e5e7eb',
              }}
            >
              <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                <StorageOutlined sx={{color: '#6b7280', mr: 1}} />
                <Typography variant="h6" sx={{fontWeight: 600}}>
                  <Translate expression="career.jobDetail.ExternalApplications.DataCollection" />
                </Typography>
              </Box>
              {
                Object.entries(interview.dataCollection).map(([key, obj]) => {
                  if (obj.value === null) return null;
                  return (
                    <Box
                      key={key}
                      sx={{
                        backgroundColor: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: 1,
                        p: 1.5,
                        mb: 1.5,
                      }}
                    >
                      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5}}>
                        <Typography variant="subtitle1" sx={{fontWeight: 700}}>
                          {key.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase())}
                        </Typography>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            backgroundColor: '#f3f4f6',
                            borderRadius: 1.5,
                            px: 2.5,
                            py: 0.5,
                            fontWeight: 600,
                            fontSize: 13,
                            color: '#6b7280e6',
                          }}
                        >
                          {String(obj.value)}
                        </Box>
                      </Box>
                      <Box sx={{display: 'grid', alignItems: 'baseline'}}>
                        <Typography variant="overline" sx={{color: '#6b7280', fontWeight: 700}}>
                          <Translate expression="career.jobDetail.ExternalApplications.Rationale" />
                        </Typography>
                        <Typography variant="body2" sx={{color: '#6b7280e6'}}>{obj.rationale}</Typography>
                      </Box>
                    </Box>
                  );
                })
              }
            </Paper>

            {
              hasEvaluationEntries &&
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  backgroundColor: '#fff',
                  borderColor: '#e5e7eb',
                }}
              >
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                  <Box sx={{display: 'flex', alignItems: 'center'}}>
                    <RuleOutlined sx={{color: '#6b7280', mr: 1}} />
                    <Typography variant="h6" sx={{fontWeight: 600}}>
                      <Translate expression="career.jobDetail.ExternalApplications.EvaluationTitle" />
                    </Typography>
                  </Box>
                  {
                    evalObj.screeningStatus &&
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        backgroundColor: '#f3f4f6',
                        color: '#6b7280',
                        border: '1px solid #e5e7eb',
                        borderRadius: 1.5,
                        px: 1.5,
                        py: 0.5,
                        fontWeight: 600,
                        fontSize: 13,
                      }}
                    >
                      {evalObj.screeningStatus}
                    </Box>
                  }
                </Box>
                {
                  evaluationEntries.map(([key, value], idx) => {
                    const section = value && typeof value === 'object' ? value : {description: String(value)};
                    const chip = evaluationChip(section.status);
                    const titleExpression = evaluationTitleMap[key];
                    const title = key.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
                    const titleContent = titleExpression ? <Translate expression={titleExpression} /> : title;

                    return (
                      <Paper
                        key={key}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 1,
                          backgroundColor: chip.bg,
                          borderColor: chip.border,
                          mb: idx < evaluationEntries.length - 1 ? 1.5 : 0,
                        }}
                      >
                        <Typography variant="subtitle1" sx={{fontWeight: 700}}>
                          {titleContent}
                        </Typography>
                        {
                          section.description &&
                          <Typography variant="body2" sx={{color: '#6b7280e6'}}>
                            {section.description}
                          </Typography>
                        }
                      </Paper>
                    );
                  })
                }
              </Paper>
            }
          </Box>
          {
            hasConversationSection &&
            <Box sx={{display: 'grid', gap: 2, alignSelf: 'start', height: 'fit-content'}}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  backgroundColor: '#fff',
                  borderColor: '#e5e7eb',
                }}
              >
                <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                  <CallOutlined sx={{color: '#6b7280', mr: 1}} />
                  <Typography variant="h6" sx={{fontWeight: 600}}>
                    <Translate expression="caregiver.CaregiverDetail.Interviews.Conversation" />
                  </Typography>
                </Box>
                {
                  hasConversation &&
                  <Box>
                    <Typography variant="overline" sx={{color: '#6b7280', fontWeight: 700}}>
                      <Translate expression="career.jobDetail.ExternalApplications.ConversationUrl" />
                    </Typography>
                    <Typography variant="body2" sx={{color: '#3a3d44ff'}}>
                      <Link
                        href={interview.convUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{color: '#1976d2', textDecoration: 'underline'}}
                      >
                        <Translate expression="caregiver.CaregiverDetail.Interviews.ViewConversation" />
                      </Link>
                    </Typography>
                  </Box>
                }
                {
                  hasRecording &&
                  <Box sx={{mt: hasConversation ? 2 : 0}}>
                    <Typography variant="overline" sx={{color: '#6b7280', fontWeight: 700}}>
                      <Translate expression="caregiver.CaregiverDetail.Interviews.Interview" />
                    </Typography>
                    <Box sx={{display: 'flex', alignItems: 'center'}}>
                      <IconButton
                        onClick={handlePlayRecordingToggle}
                        disabled={isRecordingLoading || hasRecordingError}
                        sx={{ml: -1}}
                      >
                        {
                          isRecordingPlaying ?
                            <Stop sx={{color: '#0288d1'}}/> :
                            <VolumeUp sx={{color: '#0288d1'}}/>
                        }
                      </IconButton>
                    </Box>
                  </Box>
                }
                {
                  loomInvitation &&
                  <Box sx={{mt: hasConversation ? 2 : 0}}>
                    <Typography variant="overline" sx={{color: '#6b7280', fontWeight: 700}}>
                      <Translate expression="caregiver.CaregiverDetail.Interviews.Interview" />
                    </Typography>
                    <InvitationInterviewRecording
                      invitation={loomInvitation}
                      label={translate('caregiver.CaregiverDetail.Interviews.Interview')}
                    />
                  </Box>
                }
              </Paper>
            </Box>
          }
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

InterviewCard.propTypes = {
  interview: PropTypes.object.isRequired,
};

export default InterviewCard;
